<template>
    <div>

        <ComponentDialog v-if="showComponentDialog" v-bind="componentDialogParameters"
                         v-on:hidden="showComponentDialog = false"
                         v-on:clear-selection="clearSelection"
                         v-on:click-row="setInstancePath"
                         v-on:select-component="zoomComponent"
                         v-on:zoom-component="zoomExtentsComponent"
                         v-on:zoom-reset="zoomReset"/>

        <div class="card st-framework-card">

            <div class="card-header">
                <table style="width: 100%;">
                    <tr>
                        <td style="margin: 0; padding: 0 0 0 7px; width: 40px;" v-if="environment === 'Local'">
                            <button type="button" class="btn btn-dark btn-sm" v-on:click="reloadPluginFiles(['component_instances/component_instances.rb'])">
                                <font-awesome-icon :icon="['fas','sync']"/>
                            </button>
                        </td>
                        <td style="margin: 0; padding: 0 0 0 7px; width: 40px;" v-else>
                            <button type="button" class="btn btn-dark btn-sm" v-on:click="reloadPage()">
                                <font-awesome-icon :icon="['fas','sync']"/>
                            </button>
                        </td>
                        <td style="margin: 0; padding: 0; width: 230px;">
                            {{ $i18n.tc('translations.' + name, 2).toUpperCase() }}
                        </td>
                        <template v-if="serverDataLoaded">
                            <td style="width: 1rem;"></td>
                            <template v-if="linkedToPhase">
                                <td style="padding: 0 10px; border: solid 1px lightgray; width: 30%;">
                                    {{ linkedToolData.phase.label }}
                                </td>
                                <td style="width: 1rem;"/>
                                <td style="padding: 0 10px; border: solid 1px lightgray;">
                                    <LinkAction :action="actions.workPlanLinkAction"/>
                                </td>
                                <td style="padding: 0; width: 90px; text-align: right;">
                                    (#{{ linkedToolData.phase.id }})
                                </td>
                            </template>
                            <template v-else>
                                <td style="padding: 0 10px; border: solid 1px lightgray;">
                                    <router-link :to="{name: 'work_plan_list'}" class="st-cardheader-link">
                                        {{ $i18n.t('translations.SketchUp model is not linked to a workplan phase') }}!
                                    </router-link>
                                </td>
                            </template>
                            <td style="width: 1rem;"></td>
                        </template>
                        <td v-else/>
                    </tr>
                </table>
            </div>

            <template v-if="incompatibleSketchUpPlugin">
                <div class="card-body" style="text-align: center; font-size: larger; font-weight: bold; color: red;">
                    <div>{{ $i18n.t("translations.incompatibleSketchUpPluginVersionForModule", {version: this.minimumSketchUpPluginVersion}) }}</div>
                </div>
            </template>
            <template v-else-if="serverDataLoaded">
                <div class="card-body p-0">
                    <Table :key="'component-table-'+reloadTableComponent" :reload-table="reloadTable"
                           header-class="light" header-style="font-weight: normal;" footer-class="light" body-class=""
                           with-filter with-pagination pagination-always-visible no-state-loading autofocus highlight-rows-on-click
                           :lines-per-page=crudLinesPerPage th-actions-style="width:160px;" :rows="filteredComponentList"
                           :fields="fields.table" :actions="actions.table" :parameter-data="tableParameterData" :state="state"
                           v-on:clear-selection="clearSelection"
                           v-on:click-row="clickRow"
                           v-on:detail-components="detailComponents"
                           v-on:inline-edit="toggleComponentCheck"
                           v-on:make-unique-by-id="makeUniqueById"
                           v-on:refresh="getComponentInstances"
                           v-on:renumber-definitions="renumberDefinitions"
                           v-on:reset-ignore-flipped="resetIgnoreFlipped"
                           v-on:select-all="selectAll"
                           v-on:select-components="selectComponent"
                           v-on:select-filter-options="selectFilterOptions"
                           v-on:set-ignore-flipped="setIgnoreFlipped"
                           v-on:zoom-component="zoomExtentsComponent"
                           v-on:zoom-reset="zoomReset"/>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import ComponentDialog from "@/components/dialogs/sketchup/ComponentDialog.vue";
import LinkAction from "@/components/actions/LinkAction.vue";
import SketchUp from "@/mixins/SketchUp";
import Table from "@/components/Table.vue";

/* global sketchup:false */

export default {
    name: 'Components',
    components: {
        ComponentDialog,
        LinkAction,
        Table,
    },
    props: ['name', 'icon', 'api', 'state', 'environment'],
    mixins: [SketchUp],
    data() {
        return {
            sketchUpToolData: null,
            minimumSketchUpPluginVersion: '1.26.0',
            incompatibleSketchUpPlugin: false,
            linkedToolData: null,
            serverDataLoaded: false,
            linkedToPhase: false,
            fields: null,
            actions: null,
            products: null,
            settings: null,
            crudLinesPerPage: 0,
            rawComponentList: [],
            processedComponentList: [],
            filteredComponentList: [],
            checkedComponentList: [],
            assemblyData: [],
            reloadTableComponent: 0,
            reloadTable: 0,
            tableParameterData: null,
            allDefinitionsOption: '<' + this.$i18n.tc("translations.All", 1) + '>',
            assemblyDefinitionsOption: '<' + this.$i18n.tc("translations.Assembly Component", 2) + '>',
            renumberStart: 1,
            renumberDefinitionName: null,
            filterOptions: {
                productsOnly: false,
                assemblyOnly: true,
                nonuniqueOnly: false,
                uncheckedOnly: false,
            },
            showComponentDialog: false,
            componentDialogParameters: {
                title: null,
                actions: null,
                fields: null,
                rows: null,
            },
        }
    },
    created() {
        window.vm.Tool = this;
        this.getToolData();
    },
    methods: {
        clearSelection() {
            sketchup.clearSelection();
        },
        clickRow(component) {
            let ids = component.instances.map(i => i.entity_id);
            sketchup.selectComponents(ids, true);
        },
        detailComponents(component) {
            let rows = [];
            component.instances.forEach(instance => {
                let definition_path = '';
                instance.definition_path.forEach((d, i) => {
                    definition_path += (definition_path ? ' > ' : '') + d + ' (' + instance.instance_path[i] + ')';
                });
                rows.push({
                    id: rows.length + 1,
                    entity_id: instance.entity_id,
                    instances: [{
                        instance_path: instance.instance_path,
                    }],
                    definition_path: definition_path,
                    assembly_definition_name: component.assembly_definition_name,
                    flipped: instance.is_flipped ? 'X' : '',
                });
            });
            this.componentDialogParameters.title = component.definition;
            this.componentDialogParameters.actions = this.actions.detail;
            this.componentDialogParameters.fields = this.fields.detail;
            this.componentDialogParameters.rows = rows;
            this.showComponentDialog = true;
        },
        fetchData() {
            this.$http.get(this.api + '/manage_components/' + this.sketchUpToolData.phase_id, {}).then((res) => {
                this.fields = res.data.fields;
                this.actions = res.data.actions;
                this.linkedToolData = res.data.tool_data;
                this.linkedToPhase = !!(this.linkedToolData.phase);
                this.products = res.data.products;
                this.assemblyData = res.data.assembly_data ? res.data.assembly_data : [];
                this.settings = res.data.settings;
                this.crudLinesPerPage = res.data.crudLinesPerPage;
                this.serverDataLoaded = true;
                sketchup.getComponentInstances();
            }).catch((error) => {
                console.log("Components:fetchData():error:", error);
            });
        },
        filterComponentList() {
            let assemblyDefinitions = this.settings?.assembly?.definitions ?? [];
            this.filteredComponentList = this.processedComponentList.filter(component =>
                (!this.filterOptions.nonuniqueOnly || component.hide_make_unique_by_id === false) &&
                (!this.filterOptions.productsOnly || component.attribute_naam !== null) &&
                (!this.filterOptions.assemblyOnly || assemblyDefinitions.some(d => d.name === component.definition.substring(0, component.definition.lastIndexOf("#")))) &&
                (!this.filterOptions.uncheckedOnly || component.checked === 0)
            );

            let assemblyOnly = this.filterOptions.assemblyOnly;
            this.filteredComponentList.sort(function (a, b) {
                let idxA = a.definition.lastIndexOf('#');
                let definitionA = idxA > -1 ? a.definition.substring(0, idxA) : a.definition;
                let idxB = b.definition.lastIndexOf('#');
                let definitionB = idxB > -1 ? b.definition.substring(0, idxB) : b.definition;
                if (definitionA === definitionB) {
                    let numberA = idxA > -1 ? parseInt(a.definition.substring(idxA + 1)) : -1;
                    let numberB = idxB > -1 ? parseInt(b.definition.substring(idxB + 1)) : -1;
                    if (numberA < numberB) return -1;
                    if (numberA > numberB) return 1;
                } else {
                    if (assemblyOnly) {
                        let seqA = assemblyDefinitions.findIndex(d => d.name === definitionA);
                        let seqB = assemblyDefinitions.findIndex(d => d.name === definitionB);
                        if (seqA < seqB) return -1;
                        if (seqA > seqB) return 1;
                    } else {
                        if (a.definition < b.definition) return -1;
                        if (a.definition > b.definition) return 1;
                    }
                }
                if (a.attribute_naam < b.attribute_naam) return -1;
                if (a.attribute_naam > b.attribute_naam) return 1;
                if (a.attribute_hoeveelheid < b.attribute_hoeveelheid) return -1;
                if (a.attribute_hoeveelheid > b.attribute_hoeveelheid) return 1;
                if (a.is_flipped < b.is_flipped) return -1;
                if (a.is_flipped > b.is_flipped) return 1;
                return 0;
            });

            let definitionList = [];
            this.filteredComponentList.forEach(component => {
                if (component.definition) {
                    let d = component.definition
                    let idx = d.lastIndexOf('#');
                    if (idx > 0) {
                        d = d.substring(0, idx);
                    }
                    if (!definitionList.includes(d)) {
                        definitionList.push(d);
                    }
                }
            });
            definitionList.sort(function (a, b) {
                return a.toLowerCase().localeCompare(b.toLowerCase());
            });
            definitionList.unshift(this.assemblyDefinitionsOption);
            definitionList.unshift(this.allDefinitionsOption);
            this.tableParameterData = {
                definition_options: definitionList,
            };

            this.reloadTable++;
        },
        getComponentInstances() {
            this.state.loading = true;
            sketchup.getComponentInstances();
        },
        getComponentInstancesCallback(entities) {
            this.rawComponentList = JSON.parse(entities);
            this.processComponentList();
        },
        getModelDefinitionsCallback(definitions) {
            let definitionsToRenumber;
            if (this.renumberDefinitionName === this.allDefinitionsOption) {
                definitionsToRenumber = this.getDefinitionsToRenumber(definitions, null, this.renumberStart);
            } else if (this.renumberDefinitionName === this.assemblyDefinitionsOption) {
                let assemblyDefinitions = [];
                if (this.settings?.assembly?.definitions) {
                    assemblyDefinitions = this.settings.assembly.definitions.map(d => d.name);
                }
                definitionsToRenumber = this.getDefinitionsToRenumber(definitions, assemblyDefinitions, this.renumberStart);
            } else {
                definitionsToRenumber = this.getDefinitionsToRenumber(definitions, [this.renumberDefinitionName], this.renumberStart);
            }
            sketchup.renameDefinitions(definitionsToRenumber);
        },
        getToolData() {
            this.state.loading = true;
            sketchup.getToolData();
        },
        getToolDataCallback(sketchUpToolData) {
            this.sketchUpToolData = sketchUpToolData;
            if (this.isVersionGreaterOrEqual(sketchUpToolData.plugin_version, this.minimumSketchUpPluginVersion)) {
                this.fetchData();
            } else {
                this.incompatibleSketchUpPlugin = true;
                this.state.loading = false;
            }
        },
        // makeUnique(component) {
        //     this.state.loading = true;
        //     let idx = this.processedComponentList.findIndex(item =>
        //         (item.definition === component.definition) &&
        //         (item.is_flipped !== component.is_flipped)
        //     );
        //     let instanceData = {
        //         'instance_paths': component.instances.map(i => i.instance_path),
        //         'instance_paths2': this.processedComponentList[idx].instances.map(i => i.instance_path)
        //     };
        //
        //     sketchup.makeUnique(instanceData);
        // },
        // makeUniqueCallback() {
        //     sketchup.getComponentInstances();
        // },
        makeUniqueById(component) {
            this.state.loading = true;
            let ids = component.instances.map(i => i.entity_id);
            sketchup.makeUniqueById(ids);
        },
        makeUniqueByIdCallback() {
            sketchup.getComponentInstances();
        },
        processComponentList() {
            this.$worker.run((rawComponentList, linkedToPhase, assemblyDefinitions, assemblyData, products, checkedComponentList) => {
                let newProcessedComponentList = [];

                rawComponentList.forEach(component => {
                    component.attribute_hoeveelheid = Math.round((parseFloat(component.attribute_hoeveelheid) + Number.EPSILON) * 1000) / 1000;
                    let phaseComponent = assemblyData.find(assemblyComponent => {
                        return assemblyComponent.definition === component.definition;
                    });

                    let i = newProcessedComponentList.findIndex(item =>
                        (item.definition === component.definition) &&
                        (item.attribute_naam === component.attribute_naam) &&
                        ((phaseComponent && phaseComponent.ignore_flipped === 1) || (item.is_flipped === component.is_flipped))
                    );
                    if (i === -1) {
                        let j = newProcessedComponentList.findIndex(item =>
                            (item.definition === component.definition) &&
                            (item.attribute_naam === component.attribute_naam) &&
                            (item.is_flipped === (component.is_flipped === 1 ? 0 : 1))
                        );
                        let newComponent = {
                            id: newProcessedComponentList.length + 1,
                            instances: [{
                                entity_id: component.entity_id,
                                instance_path: component.instance_path,
                                definition_path: component.definition_path,
                                is_flipped: component.is_flipped,
                            }],
                            definition: component.definition ? component.definition : '',
                            component_color: {
                                value: component.component_color,
                                label: component.component_color_name,
                            },
                            attribute_naam: component.attribute_naam,
                            attribute_hoeveelheid: component.attribute_hoeveelheid,
                            attribute_eenheid: component.attribute_eenheid,
                            is_flipped: (phaseComponent && phaseComponent.ignore_flipped === 1) ? 0 : component.is_flipped,
                            known_in_tool: (products.indexOf(component.attribute_naam) > -1 ? 'X' : ''),
                            instances_in_selection: 1,
                            amount: (component.attribute_hoeveelheid > 0 ? component.attribute_hoeveelheid : ''),
                        }

                        // flipped
                        if (phaseComponent && phaseComponent.ignore_flipped === 1) {
                            newComponent.flipped = '!';
                            newComponent.flipped_style = "background: orange; color: white;";
                        } else if (newComponent.is_flipped === 1) {
                            newComponent.flipped = 'X';
                        } else {
                            newComponent.flipped = '';
                        }

                        // set ignore_flipped
                        newComponent.ignore_flipped = (phaseComponent && phaseComponent.ignore_flipped === 1);

                        // set assembly definition
                        let name = newComponent.definition.substring(0, newComponent.definition.lastIndexOf("#"));
                        newComponent.assembly_definition = assemblyDefinitions.some(d => d.name === name);
                        newComponent.assembly_definition_name = name;

                        // set checked status
                        let checkedIdx = checkedComponentList.findIndex(item => (
                            (item.definition === newComponent.definition) &&
                            (item.is_flipped === newComponent.is_flipped))
                        );
                        if (checkedIdx === -1) {
                            newComponent.checked = 0;
                        } else {
                            newComponent.checked = 1;
                            newComponent.checked_style = 'background: #28a745; color: white;';
                        }

                        // set hide_make_unique_by_id && set_ignore_flipped
                        if (j === -1) {
                            newComponent.hide_make_unique_by_id = true;
                            newComponent.hide_set_ignore_flipped = true;
                            newComponent.hide_reset_ignore_flipped = !newComponent.ignore_flipped;
                        } else {
                            newProcessedComponentList[j].hide_make_unique_by_id = false;
                            newProcessedComponentList[j].hide_set_ignore_flipped = (!linkedToPhase || !newProcessedComponentList[j].assembly_definition);
                            newProcessedComponentList[j].hide_reset_ignore_flipped = true;
                            if (newProcessedComponentList[j].is_flipped === 1) {
                                newProcessedComponentList[j].definition_style = 'background: red; color: white;';
                            }
                            newComponent.hide_make_unique_by_id = false;
                            newComponent.hide_set_ignore_flipped = (!linkedToPhase || !newProcessedComponentList[j].assembly_definition);
                            newComponent.hide_reset_ignore_flipped = true;
                            if (newComponent.is_flipped === 1) {
                                newComponent.definition_style = 'background: red; color: white;';
                            }
                        }

                        newProcessedComponentList.push(newComponent);
                    } else {
                        newProcessedComponentList[i].instances.push({
                            entity_id: component.entity_id,
                            instance_path: component.instance_path,
                            definition_path: component.definition_path,
                            is_flipped: component.is_flipped,
                        });
                        newProcessedComponentList[i].instances_in_selection++;
                        if (component.attribute_hoeveelheid > 0) {
                            let amount = newProcessedComponentList[i].amount + component.attribute_hoeveelheid;
                            newProcessedComponentList[i].amount = Math.round((parseFloat(amount) + Number.EPSILON) * 1000) / 1000;
                        }
                    }
                });

                // make unique button will not work properly if entity_id's are shared between lines
                newProcessedComponentList.forEach(component => {
                    if (!component.hide_make_unique_by_id) {
                        let i = newProcessedComponentList.findIndex(item =>
                            (item.definition === component.definition) &&
                            (item.is_flipped !== component.is_flipped)
                        );
                        let hide_make_unique_by_id = newProcessedComponentList[i].instances.some(c1 => component.instances.findIndex(c2 => c2.entity_id === c1.entity_id) > -1);
                        newProcessedComponentList[i].hide_make_unique_by_id = hide_make_unique_by_id;
                        component.hide_make_unique_by_id = hide_make_unique_by_id;
                    }
                });

                return newProcessedComponentList;
            }, [this.rawComponentList, this.linkedToPhase, (this.settings?.assembly?.definitions ?? []), this.assemblyData, this.products, this.checkedComponentList]).then(data => {
                this.processedComponentList = data;
                this.filterComponentList();
                this.reloadTableComponent++;
                this.state.loading = false;
            }).catch(error => {
                console.log("Components:processComponentList():error:", error);
            });
        },
        renameDefinitionsCallback() {
            sketchup.getComponentInstances();
        },
        renumberDefinitions(data) {
            this.state.loading = true;
            this.renumberStart = data.start;
            this.renumberDefinitionName = data.definition;
            sketchup.getModelDefinitions();
        },
        resetIgnoreFlipped(component) {
            this.state.loading = true;
            let phaseIndex = this.assemblyData.findIndex(assemblyComponent => {
                return assemblyComponent.definition === component.definition;
            });
            if (phaseIndex !== -1) {
                delete this.assemblyData[phaseIndex].ignore_flipped;
                this.$http.put('/managed_orders/update_assembly_data/' + this.linkedToolData.phase.id, {
                    assembly_data: this.assemblyData,
                }, {}).then(() => {
                    this.processComponentList();
                }).catch((error) => {
                    console.log("Components:resetIgnoreFlipped():error:", error);
                });
            }
        },
        selectAll(data) {
            let ids = [];
            this.filteredComponentList.forEach(component => {
                if (data.ids.includes(component.id)) {
                    let component_ids = component.instances.map(i => i.entity_id);
                    ids = ids.concat(component_ids);
                }
            });
            sketchup.selectComponents(ids, true);
        },
        selectComponent(component, clear = false) {
            let ids = component.instances.map(i => i.entity_id);
            sketchup.selectComponents(ids, clear);
        },
        selectFilterOptions(choices) {
            choices.forEach(choice => {
                this.filterOptions[choice.name] = choice.value;
            });
            this.filterComponentList();
        },
        setIgnoreFlipped(component) {
            this.state.loading = true;
            let phaseIndex = this.assemblyData.findIndex(assemblyComponent => {
                return assemblyComponent.definition === component.definition;
            });
            if (phaseIndex < 0) {
                this.assemblyData.push({
                    definition: component.definition,
                    ignore_flipped: 1,
                });
            } else {
                this.assemblyData[phaseIndex].ignore_flipped = 1;
            }

            this.$http.put('/managed_orders/update_assembly_data/' + this.linkedToolData.phase.id, {
                assembly_data: this.assemblyData,
            }, {}).then(() => {
                this.processComponentList();
            }).catch((error) => {
                console.log("Components:setIgnoreFlipped():error:", error);
            });
        },
        setInstancePath(component) {
            sketchup.setInstancePath(component.instances[0].instance_path);
        },
        showOrHideLayers(component, tag, show) {
            sketchup.renderingOption('InactiveHidden', show);
            sketchup.renderingOption('InstanceHidden', show);

            if (tag) {
                sketchup.layer(tag, show);
            }
            if (component) {
                let assemblyDefinitions = this.settings?.assembly?.definitions ?? [];
                assemblyDefinitions.forEach(d => {
                    if (d.tag) {
                        sketchup.layer(d.tag, d.name === component.assembly_definition_name ? show : !show);
                    }
                });
            }
        },
        toggleComponentCheck(component) {
            let i = this.processedComponentList.findIndex(item => (item.id === component.id));
            if (component.checked === 1) {
                this.processedComponentList[i].checked_style = 'background: #28a745; color: white;';
            } else {
                delete this.processedComponentList[i].checked_style;
            }
            this.processedComponentList[i].checked = component.checked;
            i = this.checkedComponentList.findIndex(item => ((item.definition === component.definition) && (item.is_flipped === component.is_flipped)));
            if (i === -1) {
                this.checkedComponentList.push({
                    definition: component.definition,
                    is_flipped: component.is_flipped,
                });
            } else {
                this.checkedComponentList.splice(i, 1)
            }
            this.filterComponentList();
        },
        zoomComponent(component) {
            sketchup.zoomByInstancePath(component.instances[0].instance_path);
        },
        zoomExtentsComponent(component) {
            this.showOrHideLayers(component, this.settings?.assembly?.tag, true);
            sketchup.zoomExtentsByInstancePath(component.instances[0].instance_path);
        },
        zoomReset() {
            this.showOrHideLayers(null, this.settings?.assembly?.tag, false);
            sketchup.zoomExtents();
        },
    }
}
</script>

<style scoped>
</style>